import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ToastContainer, toast } from "react-toastify";
import {
  Button,
  Card,
  CardBody,
  Col,
  Container,
  Input,
  Modal,
  ModalBody,
  ModalHeader,
  Pagination,
  PaginationItem,
  PaginationLink,
  Row,
  Spinner,
} from "reactstrap";
import Breadcrumbs from "../../components/Common/Breadcrumb";
import {
  asList,
  createQuestion,
  deleteQuestion,
  getLecture,
  listQuestions,
  reorderQuestions,
  updateQuestion,
} from "./api";
import QuestionEditor, {
  makeOption,
  makeQuestion,
} from "./components/QuestionEditor";
import {
  ActionMenu,
  ConfirmDialog,
  CounterPill,
  EmptyState,
  ErrorState,
  LoadingState,
  StatusBadge,
} from "./components/Ui";
import {
  PAGE_SIZE_OPTIONS,
  QUESTION_TYPE_OPTIONS,
  SHOW_QUESTION_TYPE_PICKER,
  typeMeta,
} from "./constants";
import { PERMISSIONS, can } from "./permissions";
import { useApiCall } from "./useApiCall";
import { validateQuestion } from "./validation";
import "./questionData.css";

const num = (v) => Number(v || 0);

/** Strips UI-only keys and stamps the display order before sending. */
const toPayload = (q, index, lectureId) => ({
  lecture_id: lectureId,
  question_text: q.question_text.trim(),
  question_type: q.question_type,
  explanation: q.explanation?.trim() || "",
  difficulty_level: q.difficulty_level,
  marks: q.marks === "" || q.marks == null ? 1 : Number(q.marks),
  status: q.status,
  display_order:
    q.display_order === "" || q.display_order == null ? index + 1 : Number(q.display_order),
  correct_answer: q.correct_answer?.trim?.() || "",
  options: (q.options || []).map((o, i) => ({
    option_text: o.option_text.trim(),
    is_correct: o.is_correct ? 1 : 0,
    display_order: i + 1,
  })),
});

/** Turns a server question record back into an editable draft. */
const toDraft = (q) =>
  makeQuestion({
    key: String(q.question_id),
    question_id: q.question_id,
    question_text: q.question_text || "",
    question_type: q.question_type,
    explanation: q.explanation || "",
    difficulty_level: q.difficulty_level || "medium",
    marks: q.marks ?? 1,
    status: q.status,
    display_order: q.display_order ?? "",
    correct_answer: q.correct_answer || "",
    options: (q.options || []).map((o) =>
      makeOption(o.option_text || "", String(o.is_correct) === "1" || o.is_correct === true)
    ),
  });

const LectureQuestionsPage = () => {
  const { bankId, unitId, lectureId } = useParams();
  const navigate = useNavigate();
  const call = useApiCall();

  const [lecture, setLecture] = useState(null);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [search, setSearch] = useState("");
  const [typeFilter, setTypeFilter] = useState("all");
  const difficultyFilter = "all";
  const statusFilter = "all";
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  // Single add / edit
  const [singleModal, setSingleModal] = useState(null); // { mode, draft }
  const [singleErrors, setSingleErrors] = useState({});

  const [saving, setSaving] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);

  document.title = lecture?.name
    ? `${lecture.name} | Question Bank`
    : "Lecture Questions | Medicotoon";

  /* ---------------------------------------------------------------- */
  /* Loading                                                           */
  /* ---------------------------------------------------------------- */
  const fetchQuestions = useCallback(async () => {
    const { ok, data } = await call(() => listQuestions({ lecture_id: lectureId }), {
      silent: true,
    });
    if (ok) {
      setQuestions(
        asList(data)
          .rows.slice()
          .sort((a, b) => num(a.display_order) - num(b.display_order))
      );
    }
    return ok;
  }, [call, lectureId]);

  const fetchAll = useCallback(async () => {
    setLoading(true);
    setLoadError(null);

    const lectureRes = await call(() => getLecture(lectureId), { silent: true });
    if (!lectureRes.ok) {
      setLoadError(lectureRes.message || "Failed to load the lecture");
      setLoading(false);
      return;
    }
    setLecture(
      Array.isArray(lectureRes.data) ? lectureRes.data[0] : lectureRes.data
    );

    const ok = await fetchQuestions();
    if (!ok) setLoadError("Failed to load questions");
    setLoading(false);
  }, [call, lectureId, fetchQuestions]);

  useEffect(() => {
    fetchAll();
  }, [fetchAll]);

  /* ---------------------------------------------------------------- */
  /* Derived list                                                      */
  /* ---------------------------------------------------------------- */
  const filtered = useMemo(() => {
    const term = search.trim().toLowerCase();
    return questions.filter((q) => {
      const matchesTerm =
        !term || String(q.question_text || "").toLowerCase().includes(term);
      const matchesType = typeFilter === "all" || q.question_type === typeFilter;
      const matchesDifficulty =
        difficultyFilter === "all" || q.difficulty_level === difficultyFilter;
      const matchesStatus =
        statusFilter === "all" || String(q.status) === statusFilter;
      return matchesTerm && matchesType && matchesDifficulty && matchesStatus;
    });
  }, [questions, search, typeFilter, difficultyFilter, statusFilter]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / pageSize));
  const currentPage = Math.min(page, totalPages);
  const pageRows = useMemo(
    () => filtered.slice((currentPage - 1) * pageSize, currentPage * pageSize),
    [filtered, currentPage, pageSize]
  );

  useEffect(() => {
    setPage(1);
  }, [search, typeFilter, pageSize]);

  /* ---------------------------------------------------------------- */
  /* Single add / edit                                                 */
  /* ---------------------------------------------------------------- */
  const openAddSingle = () => {
    setSingleErrors({});
    setSingleModal({
      mode: "add",
      draft: makeQuestion({ display_order: questions.length + 1 }),
    });
  };

  const openEditSingle = (question) => {
    setSingleErrors({});
    setSingleModal({ mode: "edit", draft: toDraft(question) });
  };

  const submitSingle = async () => {
    const draft = singleModal.draft;
    const errors = validateQuestion({ ...draft, lecture_id: lectureId });
    setSingleErrors(errors);
    if (Object.keys(errors).length) {
      toast.error("Please fix the highlighted fields");
      return;
    }

    setSaving(true);
    // lecture_id is stamped here, so a question created from inside a lecture
    // is associated with it automatically - never as a separate step.
    const payload = toPayload(draft, questions.length, lectureId);
    const { ok } =
      singleModal.mode === "add"
        ? await call(() => createQuestion(payload), { success: "Question added" })
        : await call(
            () => updateQuestion({ ...payload, question_id: draft.question_id }),
            { success: "Question updated" }
          );
    setSaving(false);

    if (ok) {
      setSingleModal(null);
      fetchQuestions();
    }
  };

  /* ---------------------------------------------------------------- */
  /* Row actions                                                       */
  /* ---------------------------------------------------------------- */
  const moveQuestion = async (question, direction) => {
    const ordered = [...questions];
    const index = ordered.findIndex((q) => q.question_id === question.question_id);
    const target = direction === "up" ? index - 1 : index + 1;
    if (index < 0 || target < 0 || target >= ordered.length) return;

    [ordered[index], ordered[target]] = [ordered[target], ordered[index]];
    setQuestions(ordered.map((q, i) => ({ ...q, display_order: i + 1 })));

    const { ok } = await call(
      () => reorderQuestions(lectureId, ordered.map((q) => q.question_id)),
      { error: "Failed to reorder questions" }
    );
    if (!ok) fetchQuestions();
  };

  const confirmDelete = async () => {
    setSaving(true);
    const { ok } = await call(() => deleteQuestion(deleteTarget.question_id), {
      success: "Question deleted",
    });
    setSaving(false);
    if (ok) {
      setDeleteTarget(null);
      fetchQuestions();
    }
  };

  /* ---------------------------------------------------------------- */
  /* Render                                                            */
  /* ---------------------------------------------------------------- */
  if (!can(PERMISSIONS.BANK_VIEW)) {
    return (
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs title="Question Data" breadcrumbItem="Lecture" />
          <EmptyState
            icon="bx bx-lock-alt"
            title="Access denied"
            description="You do not have permission to view question banks."
          />
        </Container>
      </div>
    );
  }

  return (
    <React.Fragment>
      <div className="page-content">
        <Container fluid>
          <Breadcrumbs
            title="Question Data"
            breadcrumbItem={lecture?.name || "Lecture"}
          />

          <Row>
            <Col lg={12}>
              <Card>
                <CardBody>
                  {loading ? (
                    <LoadingState label="Loading lecture..." />
                  ) : loadError ? (
                    <ErrorState message={loadError} onRetry={fetchAll} />
                  ) : (
                    <>
                      <div className="qd-toolbar">
                        <div>
                          <Button
                            color="light"
                            size="sm"
                            className="mb-2"
                            onClick={() =>
                              navigate(`/question-data/banks/${bankId}`)
                            }
                          >
                            <i className="bx bx-arrow-back me-1" />
                            Back to bank
                          </Button>
                          <h4 className="mb-1">
                            {lecture?.name} <StatusBadge status={lecture?.status} />
                          </h4>
                          <p className="text-muted mb-2">
                            {lecture?.description || "No description"}
                          </p>
                          <div className="qd-toolbar-stats">
                            <CounterPill
                              icon="bx bx-help-circle"
                              count={questions.length}
                              label="questions"
                            />
                            <span className="qd-counter text-muted">
                              Lecture ID: {lectureId} &middot; Unit ID: {unitId}
                            </span>
                          </div>
                        </div>

                        <div className="qd-toolbar-actions">
                          {can(PERMISSIONS.QUESTION_CREATE) ? (
                            <>
                              <Button color="primary" onClick={openAddSingle}>
                                <i className="bx bx-plus me-1" />
                                Add Question
                              </Button>
                            </>
                          ) : null}
                        </div>
                      </div>

                      <Row className="g-2 mt-3">
                        <Col md={7}>
                          <Input
                            type="text"
                            placeholder="Search questions..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                          />
                        </Col>
                        {SHOW_QUESTION_TYPE_PICKER ? (
                        <Col md={3}>
                          <Input
                            type="select"
                            value={typeFilter}
                            onChange={(e) => setTypeFilter(e.target.value)}
                          >
                            <option value="all">All types</option>
                            {QUESTION_TYPE_OPTIONS.map((o) => (
                              <option key={o.value} value={o.value}>
                                {o.label}
                              </option>
                            ))}
                          </Input>
                        </Col>
                        ) : null}
                        <Col md={2}>
                          <Input
                            type="select"
                            value={pageSize}
                            onChange={(e) => setPageSize(Number(e.target.value))}
                          >
                            {PAGE_SIZE_OPTIONS.map((n) => (
                              <option key={n} value={n}>
                                {n}
                              </option>
                            ))}
                          </Input>
                        </Col>
                      </Row>

                      <div className="mt-3">
                        {!questions.length ? (
                          <EmptyState
                            icon="bx bx-help-circle"
                            title="No questions in this lecture yet"
                            description="Add the first question to this lecture."
                            action={
                              can(PERMISSIONS.QUESTION_CREATE) ? (
                                <div className="d-flex gap-2 justify-content-center flex-wrap">
                                  <Button color="primary" onClick={openAddSingle}>
                                    <i className="bx bx-plus me-1" />
                                    Add Question
                                  </Button>
                                </div>
                              ) : null
                            }
                          />
                        ) : !filtered.length ? (
                          <EmptyState
                            icon="bx bx-search"
                            title="No matching questions"
                            description="Try a different search term or clear the filters."
                          />
                        ) : (
                          pageRows.map((question, index) => {
                            const absoluteIndex = questions.findIndex(
                              (q) => q.question_id === question.question_id
                            );
                            const meta = typeMeta(question.question_type);
                            return (
                              <div className="qd-question-row" key={question.question_id}>
                                <div className="qd-question-index">
                                  {num(question.display_order) ||
                                    (currentPage - 1) * pageSize + index + 1}
                                </div>

                                <div className="qd-question-body">
                                  <div className="qd-question-text">
                                    {question.question_text}
                                  </div>
                                  <div className="qd-question-tags">
                                    <span className="badge bg-light text-dark">
                                      {meta.label}
                                    </span>
                                  </div>
                                  {meta.hasOptions && question.options?.length ? (
                                    <ul className="qd-question-options">
                                      {question.options.map((o, i) => {
                                        const correct =
                                          String(o.is_correct) === "1" ||
                                          o.is_correct === true;
                                        return (
                                          <li
                                            key={o.option_id || i}
                                            className={correct ? "is-correct" : ""}
                                          >
                                            <i
                                              className={`bx ${
                                                correct ? "bx-check-circle" : "bx-circle"
                                              }`}
                                            />
                                            {o.option_text}
                                          </li>
                                        );
                                      })}
                                    </ul>
                                  ) : null}
                                </div>

                                <div className="qd-node-actions">
                                  {can(PERMISSIONS.QUESTION_UPDATE) ? (
                                    <>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-light"
                                        title="Move up"
                                        disabled={absoluteIndex === 0}
                                        onClick={() => moveQuestion(question, "up")}
                                      >
                                        <i className="bx bx-up-arrow-alt" />
                                      </button>
                                      <button
                                        type="button"
                                        className="btn btn-sm btn-light"
                                        title="Move down"
                                        disabled={absoluteIndex === questions.length - 1}
                                        onClick={() => moveQuestion(question, "down")}
                                      >
                                        <i className="bx bx-down-arrow-alt" />
                                      </button>
                                    </>
                                  ) : null}
                                  <ActionMenu
                                    actions={[
                                      {
                                        key: "edit",
                                        label: "Edit",
                                        icon: "bx bx-edit",
                                        hidden: !can(PERMISSIONS.QUESTION_UPDATE),
                                        onClick: () => openEditSingle(question),
                                      },
                                      { key: "d1", divider: true },
                                      {
                                        key: "delete",
                                        label: "Delete",
                                        icon: "bx bx-trash",
                                        danger: true,
                                        hidden: !can(PERMISSIONS.QUESTION_DELETE),
                                        onClick: () => setDeleteTarget(question),
                                      },
                                    ]}
                                  />
                                </div>
                              </div>
                            );
                          })
                        )}
                      </div>

                      {filtered.length > pageSize ? (
                        <div className="d-flex justify-content-between align-items-center mt-3 flex-wrap gap-2">
                          <span className="text-muted small">
                            Showing {(currentPage - 1) * pageSize + 1}-
                            {Math.min(currentPage * pageSize, filtered.length)} of{" "}
                            {filtered.length}
                          </span>
                          <Pagination className="mb-0">
                            <PaginationItem disabled={currentPage === 1}>
                              <PaginationLink
                                previous
                                onClick={() => setPage(currentPage - 1)}
                              />
                            </PaginationItem>
                            {Array.from({ length: totalPages }, (_, i) => i + 1).map(
                              (p) => (
                                <PaginationItem key={p} active={p === currentPage}>
                                  <PaginationLink onClick={() => setPage(p)}>
                                    {p}
                                  </PaginationLink>
                                </PaginationItem>
                              )
                            )}
                            <PaginationItem disabled={currentPage === totalPages}>
                              <PaginationLink
                                next
                                onClick={() => setPage(currentPage + 1)}
                              />
                            </PaginationItem>
                          </Pagination>
                        </div>
                      ) : null}
                    </>
                  )}
                </CardBody>
              </Card>
            </Col>
          </Row>
        </Container>
      </div>

      {/* Single question */}
      <Modal
        isOpen={!!singleModal}
        toggle={() => !saving && setSingleModal(null)}
        centered
        size="lg"
      >
        <ModalHeader toggle={() => !saving && setSingleModal(null)}>
          {singleModal?.mode === "add" ? "Add Question" : "Edit Question"}
          <div className="text-muted small fw-normal">
            Saving into lecture: {lecture?.name}
          </div>
        </ModalHeader>
        <ModalBody>
          {singleModal ? (
            <>
              <QuestionEditor
                value={singleModal.draft}
                errors={singleErrors}
                onChange={(draft) => setSingleModal({ ...singleModal, draft })}
              />
              <div className="d-flex justify-content-end gap-2 mt-3">
                <Button
                  color="light"
                  onClick={() => setSingleModal(null)}
                  disabled={saving}
                >
                  Cancel
                </Button>
                <Button color="primary" onClick={submitSingle} disabled={saving}>
                  {saving ? <Spinner size="sm" className="me-1" /> : null}
                  {singleModal.mode === "add" ? "Add question" : "Save changes"}
                </Button>
              </div>
            </>
          ) : null}
        </ModalBody>
      </Modal>

      <ConfirmDialog
        isOpen={!!deleteTarget}
        title="Delete question"
        message="Delete this question? This cannot be undone."
        busy={saving}
        onConfirm={confirmDelete}
        onCancel={() => setDeleteTarget(null)}
      />

      <ToastContainer />
    </React.Fragment>
  );
};

export default LectureQuestionsPage;
